import React, { useState, useRef, useEffect, Fragment } from 'react';
import { API_BASE_URL } from '../../config';
import './AIReportSearchBar.css';
import ChatAnnotatorIntegration from '../../shared/ChatAnnotatorIntegration';

const AIReportSearchBar = () => {
  const [query, setQuery] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [conversation, setConversation] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const conversationEndRef = useRef(null);
  const inputRef = useRef(null);

  const exampleQueries = [
    "Show me revenue trends for the last 6 months",
    "Which properties have the lowest occupancy rates?",
    "Generate a report on tenant payment performance",
    "Compare property performance across all locations",
    "What are the top 5 tenants by revenue?",
    "Show me application conversion rates",
    "Generate a custom report for Q1 2024",
    "Which properties need attention for maintenance?"
  ];

  useEffect(() => {
    // Add welcome message
    setConversation([
      {
        type: 'assistant',
        message: "Hi! I can help you generate custom reports and analyze your data. Try asking me questions like 'Show me revenue trends' or 'Which properties have low occupancy?'"
      }
    ]);
  }, []);

  useEffect(() => {
    // Auto-scroll to bottom when conversation updates
    conversationEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!query.trim() || isProcessing) return;

    const userMessage = query.trim();
    setQuery('');
    setShowSuggestions(false);

    // Add user message to conversation
    setConversation(prev => [...prev, { type: 'user', message: userMessage }]);
    setIsProcessing(true);

    try {
      // Build history for backend (last 5 exchanges for Groq context)
      const history = conversation
        .filter((m) => m.type && m.message)
        .map((m) => ({ role: m.type === 'user' ? 'user' : 'assistant', content: m.message }));

      const response = await fetch(`${API_BASE_URL}/api/reports/custom-query`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: userMessage,
          context: 'reporting',
          history,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to process query');
      }

      const data = await response.json();
      
      // Use response from backend (will be enhanced with ChatAnnotator/OpenAI)
      const aiResponse = data.response || generatePlaceholderResponse(userMessage);
      
      setConversation(prev => [...prev, { type: 'assistant', message: aiResponse }]);
      setIsProcessing(false);
    } catch (error) {
      console.error('Error processing query:', error);
      const errorMessage = generatePlaceholderResponse(userMessage);
      setConversation(prev => [...prev, { type: 'assistant', message: errorMessage }]);
      setIsProcessing(false);
    }
  };

  const generatePlaceholderResponse = (userQuery) => {
    const lowerQuery = userQuery.toLowerCase();
    
    if (lowerQuery.includes('revenue') || lowerQuery.includes('income')) {
      return "I understand you're asking about revenue data. Once the backend is connected, I'll fetch real revenue trends from your database and provide detailed analytics. For now, you can view revenue metrics in the dashboard above.";
    } else if (lowerQuery.includes('occupancy') || lowerQuery.includes('vacancy')) {
      return "Occupancy analysis is coming soon! When connected to the backend, I'll analyze occupancy rates across all properties and identify trends and opportunities.";
    } else if (lowerQuery.includes('tenant') && (lowerQuery.includes('payment') || lowerQuery.includes('performance'))) {
      return "Tenant payment performance reports will be available once the backend integration is complete. I'll analyze payment history, on-time rates, and generate comprehensive reports.";
    } else if (lowerQuery.includes('property') && (lowerQuery.includes('compare') || lowerQuery.includes('performance'))) {
      return "Property comparison reports are planned! Once implemented, I'll compare properties across multiple metrics including revenue, occupancy, maintenance costs, and tenant satisfaction.";
    } else if (lowerQuery.includes('application') || lowerQuery.includes('conversion')) {
      return "Application and conversion analytics will help you understand your applicant pipeline. The backend will track application sources, conversion rates, and time-to-lease metrics.";
    } else if (lowerQuery.includes('custom') || lowerQuery.includes('report')) {
      return "Custom report generation is a key feature! Once connected to the backend, I'll allow you to create reports with specific date ranges, properties, metrics, and filters tailored to your needs.";
    } else {
      return "I'm ready to help you generate custom reports! Once the backend integration is complete, I'll be able to query your databases, analyze trends, and even pull relevant information from the internet. What specific data are you looking for?";
    }
  };

  const handleSuggestionClick = (suggestion) => {
    setQuery(suggestion);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowSuggestions(false);
    }
  };

  const formatMessage = (message) => {
    // Simple formatting for better readability
    return message.split('\n').map((line, index) => (
      <Fragment key={index}>
        {line}
        {index < message.split('\n').length - 1 && <br />}
      </Fragment>
    ));
  };

  return (
    <div className="ai-report-search-container">
      {/* Conversation Area */}
      <div className="conversation-area">
        {conversation.map((item, index) => (
          <div key={index} className={`conversation-message ${item.type}`}>
            <div className="message-avatar">
              {item.type === 'user' ? '👤' : '🤖'}
            </div>
            <div className="message-content">
              <div className="message-text">
                {formatMessage(item.message)}
              </div>
            </div>
          </div>
        ))}
        {isProcessing && (
          <div className="conversation-message assistant">
            <div className="message-avatar">🤖</div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}
        <div ref={conversationEndRef} />
      </div>

      {/* Search Input Area */}
      <div className="search-input-area">
        <form onSubmit={handleSubmit} className="search-form">
          <div className="input-wrapper">
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setShowSuggestions(e.target.value.trim().length > 0);
              }}
              onFocus={() => {
                if (exampleQueries.length > 0) setShowSuggestions(true);
              }}
              onKeyDown={handleKeyDown}
              placeholder="Ask for a custom report... (e.g., 'Show me revenue trends for Q1')"
              className="ai-search-input"
              disabled={isProcessing}
            />
            <button
              type="submit"
              className="send-button"
              disabled={!query.trim() || isProcessing}
            >
              {isProcessing ? (
                <span className="loading-spinner-small">⟳</span>
              ) : (
                <span>➤</span>
              )}
            </button>
          </div>
        </form>

        {/* Example Suggestions */}
        {showSuggestions && exampleQueries.length > 0 && (
          <div className="suggestions-container">
            <div className="suggestions-header">Try asking:</div>
            <div className="suggestions-list">
              {exampleQueries
                .filter(q => q.toLowerCase().includes(query.toLowerCase()) || !query.trim())
                .slice(0, 4)
                .map((suggestion, index) => (
                  <button
                    key={index}
                    className="suggestion-item"
                    onClick={() => handleSuggestionClick(suggestion)}
                  >
                    <span className="suggestion-icon">💡</span>
                    {suggestion}
                  </button>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIReportSearchBar;

